import i18n from "../../../../../i18n";
import { canManagePushDashboardDefaults } from "./access";

const ENDPOINT = "client-configurations/push-dashboard-defaults";

interface ApiResponse {
  ok: boolean;
  json: () => Promise<{
    error?: string | { message?: string };
  } | null>;
}

type Api = (
  path: string,
  options: { method: string; body: string; headers: Record<string, string> }
) => Promise<ApiResponse>;

/**
 * Saves the Daily Dashboard defaults of the PUSH tab. The form only carries the field when the
 * section was edited, so an untouched section makes no request.
 *
 * Sent as JSON: the fetcher turns an object body into bracket-notation form fields that the API's
 * body parser does not rebuild (nested objects, arrays, an empty object). `api()` does not throw
 * on an error status, so the response is checked and the API's message is shown to the coach.
 */
export const savePushDashboardDefaults = async (
  body: { pushDashboardDefaults?: string },
  locals: Parameters<typeof canManagePushDashboardDefaults>[0],
  api: Api
): Promise<void> => {
  if (body.pushDashboardDefaults === undefined) return;
  if (!canManagePushDashboardDefaults(locals)) return;

  let defaults: unknown;
  try {
    defaults = JSON.parse(body.pushDashboardDefaults);
  } catch {
    throw new Error(
      i18n.t("dashboard:pushDailyDashboard.errors.invalidPayload")
    );
  }

  const response = await api(ENDPOINT, {
    method: "PUT",
    body: JSON.stringify(defaults),
    headers: { "Content-Type": "application/json" },
  });
  if (response.ok) return;

  // msb-api answers `{ error: { message, key } }`
  const payload = await response.json().catch(() => null);
  const apiMessage =
    typeof payload?.error === "string"
      ? payload.error
      : payload?.error?.message;
  throw new Error(
    apiMessage || i18n.t("dashboard:pushDailyDashboard.errors.saveFailed")
  );
};
